import React, { useState } from 'react';
import {
  Box,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function FeatureList({
  part_id = '',
  features = [],
  corrections_active = 0,
  onSelectFeature
}) {
  const [expanded, setExpanded] = useState(true);
  const [selectedFeatId, setSelectedFeatId] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  const handleSelect = (feat) => {
    setSelectedFeatId(feat.id);
    if (onSelectFeature) onSelectFeature(feat);
    setToastMessage(`CAD Viewport: Highlighted ${feat.name} (${feat.id})`);
    setTimeout(() => setToastMessage(''), 2500);
  };

  return (
    <div className="border border-slate-200 bg-white rounded-xl shadow-xs overflow-hidden my-3 text-slate-800 animate-in fade-in duration-200">
      {/* Header */}
      <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">
            <Box className="w-3.5 h-3.5" />
          </div>
          <div>
            <h4 className="font-bold text-xs text-slate-900 tracking-tight flex items-center gap-2">
              <span>Recognized 3D CAD Features</span>
              {part_id && (
                <span className="font-mono text-[10px] text-slate-400 font-normal">
                  {part_id}
                </span>
              )}
            </h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {features.length > 0 ? `${features.length} Classified Features` : 'B-Rep Feature Recognition'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {corrections_active > 0 && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              {corrections_active} Adjusted
            </span>
          )}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors cursor-pointer"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="bg-blue-600 text-white text-[11px] font-medium px-3 py-1.5 flex items-center justify-between animate-in fade-in">
          <span>{toastMessage}</span>
          <span className="text-[10px] opacity-80">Interactive 3D Face Union</span>
        </div>
      )}

      {/* Feature Items List */}
      {expanded && (
        <div className="p-2 divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {features.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400 italic">
              No features classified yet.
            </div>
          ) : (
            features.map((feat) => {
            const isSelected = selectedFeatId === feat.id;
            const confPct = Math.round((feat.confidence || 0.95) * 100);
            const isHighConf = confPct >= 90;

            return (
              <div
                key={feat.id}
                onClick={() => handleSelect(feat)}
                className={`py-2 px-3 rounded-lg flex items-center justify-between cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-blue-50/80 text-blue-900 font-semibold'
                    : 'hover:bg-slate-50 text-slate-800'
                } ${feat.excluded ? 'opacity-40 line-through' : ''}`}
              >
                {/* Left: Code, Category, Faces */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="font-mono font-bold text-xs text-blue-600 shrink-0">
                    {feat.id}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold text-slate-900 truncate">
                        {feat.name}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-100 text-slate-600 font-mono">
                        {feat.category}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono truncate mt-0.5">
                      Faces: {feat.face_ids ? feat.face_ids.slice(0, 5).join(', ') : ''}
                      {feat.face_ids && feat.face_ids.length > 5 ? '...' : ''}
                    </div>
                  </div>
                </div>

                {/* Right: Confidence badge & Focus button */}
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`text-[10px] font-mono font-bold px-1.5 py-0.2 rounded ${
                      isHighConf
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}
                  >
                    {confPct}% conf
                  </span>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(feat);
                    }}
                    className="p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-colors"
                    title="Focus feature in 3D"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          }))}
        </div>
      )}
    </div>
  );
}
